import GraphViewsSelect from '@/circle/components/GraphViewsSelect'
import IconTextButton from '@/common/atoms/IconTextButton'
import { Title } from '@/common/atoms/Title'
import useCopyUrl from '@/common/hooks/useCopyUrl'
import {
  parseOrgGraphView,
  useDisabledGraphViews,
} from '@/graph/instanceGraphViews'
import { CirclesGraphViews } from '@/graph/types'
import { defaultGraphView } from '@rolebase/shared/model/graph'
import {
  Button,
  Collapse,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react'
import { Governance_Mode_Enum, useUpdateOrgMutation } from '@gql'
import { yupResolver } from '@hookform/resolvers/yup'
import { getOrgPath } from '@rolebase/shared/helpers/getOrgPath'
import { getStrictSwitchSummary } from '@rolebase/shared/helpers/getStrictSwitchSummary'
import { EntityChangeType, LogType } from '@rolebase/shared/model/log'
import { ProposalDecisionMode } from '@rolebase/shared/model/proposal'
import { nameSchema } from '@rolebase/shared/schemas'
import React, { useCallback, useEffect, useMemo } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { CopyIcon, EditIcon } from 'src/icons'
import settings from 'src/settings'
import * as yup from 'yup'
import useCreateLog from '@/log/hooks/useCreateLog'
import useOrgAdmin from '@/member/hooks/useOrgAdmin'
import useOrgOwner from '@/member/hooks/useOrgOwner'
import { trpc } from 'src/trpc'
import useOrg from '../hooks/useOrg'
import { useOrgContext, useOrgEditActions } from '@/org/contexts/OrgContext'
import Switch from '@/common/atoms/Switch'
import AsyncGovernanceSwitch from '../components/AsyncGovernanceSwitch'
import GovernanceModeSelect from '../components/GovernanceModeSelect'
import OrgIconEdit from '../components/OrgIconEdit'
import ProposalDecisionModesCheckboxes from '../components/ProposalDecisionModesCheckboxes'
import ProposalDurationInput, {
  maxProposalDurationDays,
  minProposalDurationDays,
} from '../components/ProposalDurationInput'
import OrgDeleteModal from '../modals/OrgDeleteModal'
import OrgSlugModal from '../modals/OrgSlugModal '
import StrictGovernanceModal, {
  StrictConfirmResult,
  StrictGovernanceRoleToCreate,
} from '../modals/StrictGovernanceModal'

interface Values {
  name: string
  governanceMode: Governance_Mode_Enum
  defaultGraphView: CirclesGraphViews
  asyncGovernanceEnabled: boolean
  proposalDurationDays: number
  proposalDecisionModes: ProposalDecisionMode[]
  defaultGraphFolded: boolean
  defaultGraphMembers: boolean
  meetingAidsEnabled: boolean
}

const resolver = yupResolver(
  yup.object().shape({
    name: nameSchema.required(),
    proposalDurationDays: yup
      .number()
      .integer()
      .min(minProposalDurationDays)
      .max(maxProposalDurationDays)
      .required(),
  })
)

export default function OrgSettingsPage() {
  const { orgId, orgData } = useOrgContext()
  const editActions = useOrgEditActions()
  const createLog = useCreateLog()
  const org = useOrg(orgId)
  const disabledViews = useDisabledGraphViews()
  const { t } = useTranslation()
  const toast = useToast()
  const isOwner = useOrgOwner()
  const isAdmin = useOrgAdmin()
  const [editOrg] = useUpdateOrgMutation()

  const deleteModal = useDisclosure()
  const slugModal = useDisclosure()
  const strictModal = useDisclosure()

  const {
    handleSubmit,
    register,
    control,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<Values>({ resolver })

  // Init form data
  useEffect(() => {
    if (!org) return
    const graphView =
      parseOrgGraphView(
        disabledViews,
        org.defaultGraphView,
        org.defaultGraphFolded,
        org.defaultGraphMembers
      ) || defaultGraphView
    reset({
      name: org.name,
      defaultGraphView: graphView.view,
      defaultGraphFolded: graphView.folded,
      defaultGraphMembers: graphView.members,
      governanceMode: org.governanceMode,
      asyncGovernanceEnabled: org.asyncGovernanceEnabled,
      proposalDurationDays: org.proposalDurationDays,
      proposalDecisionModes: org.proposalDecisionModes,
      meetingAidsEnabled: org.meetingAidsEnabled,
    })
  }, [org])

  // The duration and the open modes only mean something when async voting is on.
  // defaultValue is what this reads until the form notifies it: the org comes
  // from a synchronous store, so the reset above fires before useWatch has
  // subscribed, and the value it would have carried is lost.
  const asyncGovernanceEnabled = useWatch({
    control,
    name: 'asyncGovernanceEnabled',
    defaultValue: org?.asyncGovernanceEnabled,
  })

  const showSavedToast = useCallback(() => {
    toast({
      title: t('Settings.toastSaved'),
      status: 'success',
      duration: 3000,
      isClosable: true,
    })
  }, [toast, t])

  // Entering Strict pauses on a confirmation window before the mode itself
  // changes, but editOrg below has already written the rest of the form —
  // so this says exactly that, instead of the generic "saved" that would
  // otherwise land at the same moment the window asks for a decision.
  const showPendingStrictToast = useCallback(() => {
    toast({
      title: t('Settings.toastSavedPendingStrict'),
      status: 'success',
      duration: 3000,
      isClosable: true,
    })
  }, [toast, t])

  const onSubmit = handleSubmit(async ({ governanceMode, ...values }) => {
    await editOrg({ variables: { id: orgId!, values } })

    // Governance mode is Owner-only and goes through a dedicated backend mutation
    const changesMode = isOwner && governanceMode !== org?.governanceMode
    const entersStrict =
      changesMode && governanceMode === Governance_Mode_Enum.Strict

    // Entering Strict is the one transition that stops here: the confirmation
    // window takes over, and setGovernanceMode is only called once it
    // resolves (see handleStrictConfirm). Leaving Strict, or any other
    // transition, keeps today's behavior exactly (GOUV-03: it is the
    // legitimate, visible escape hatch D6 relies on).
    //
    // editOrg above already wrote the rest of the form (name, default view,
    // proposal settings) regardless of what happens to the mode next, so a
    // toast fires here too — but not the generic "saved" one, since the one
    // setting the person is actually watching (the mode) hasn't moved yet.
    // handleStrictConfirm speaks for itself afterwards, on success as on
    // failure; canceling stops here, with this toast for the whole journey.
    if (entersStrict) {
      showPendingStrictToast()
      strictModal.onOpen()
      return
    }

    if (changesMode) {
      await trpc.org.setGovernanceMode.mutate({ orgId: orgId!, governanceMode })
    }

    showSavedToast()
  })

  // Confirmed: create the checked base roles first — once in Strict, a base
  // role only changes by proposal, owner included — then switch the mode.
  //
  // Both the role creation (an Apollo mutation) and setGovernanceMode (a
  // tRPC call) reject on failure rather than returning a falsy value — there
  // is no errorPolicy configured, so a network or GraphQL error throws. The
  // try/catch below is what actually catches that; it also reports which
  // roles made it into the database before the failure, so the modal can
  // uncheck them and a retry only attempts what's still missing. No rollback
  // of what was created — kept deliberately out of scope.
  const handleStrictConfirm = useCallback(
    async (
      rolesToCreate: StrictGovernanceRoleToCreate[]
    ): Promise<StrictConfirmResult> => {
      if (!orgId) return { createdNames: [] }

      const createdNames: string[] = []
      try {
        for (const roleToCreate of rolesToCreate) {
          const role = await editActions.createRole({
            name: roleToCreate.name,
            base: true,
            parentLink: roleToCreate.parentLink,
            singleMember: roleToCreate.singleMember,
            colorHue: roleToCreate.colorHue,
          })
          if (!role) {
            // orgId vanished mid-flight (the one case createRole itself
            // reports instead of throwing) — treated the same as a
            // rejection below.
            throw new Error('createRole returned no role')
          }
          createdNames.push(roleToCreate.name)

          // Logged the same way BaseRoleCreateModal does, so the org chart
          // keeps track of what the switch added.
          createLog({
            display: { type: LogType.RoleCreate, id: role.id, name: role.name },
            changes: {
              roles: [
                { type: EntityChangeType.Create, id: role.id, data: role },
              ],
            },
          })
        }

        await trpc.org.setGovernanceMode.mutate({
          orgId,
          governanceMode: Governance_Mode_Enum.Strict,
        })
        strictModal.onClose()
        // Locking the whole org is the heaviest action of this page: it says so,
        // like every other save here. The window closing is not an answer.
        showSavedToast()
        return { createdNames }
      } catch (error) {
        console.error(error)
        toast({
          title: t('common.error'),
          description: t('common.errorRetry'),
          status: 'error',
        })
        return { createdNames }
      }
    },
    [orgId, editActions, createLog, strictModal, showSavedToast, toast, t]
  )

  // Canceled: nothing was created, the mode never changed — only the select
  // still shows Strict, so it is put back to the org's real mode.
  const handleStrictCancel = useCallback(() => {
    if (org) setValue('governanceMode', org.governanceMode)
    strictModal.onClose()
  }, [org, setValue, strictModal])

  // URL
  const url = settings.url + (org ? getOrgPath(org) : '')
  const copyUrl = useCopyUrl(url)

  const strictSwitchSummary = useMemo(
    () =>
      orgData && orgId ? getStrictSwitchSummary(orgData, orgId) : undefined,
    [orgData, orgId]
  )

  if (!org || !orgId) return null

  return (
    <>
      <Title>{t('Settings.orgSettings')}</Title>

      <form onSubmit={onSubmit}>
        <VStack spacing={10} align="start" maxW="xl">
          <Heading as="h1" size="lg">
            {t('Settings.orgSettings')}
          </Heading>
          <FormControl isInvalid={!!errors.name}>
            <FormLabel>{t('common.name')}</FormLabel>
            <Input {...register('name')} autoComplete="off" />
          </FormControl>

          <FormControl>
            <FormLabel>{t('OrgEditModal.icon')}</FormLabel>
            <OrgIconEdit id={orgId} icon={org.icon} name={org.name} />
            <FormHelperText>{t('OrgEditModal.iconHelp')}</FormHelperText>
          </FormControl>

          <FormControl>
            <FormLabel>{t('OrgEditModal.slug')}</FormLabel>
            <Flex>
              <InputGroup>
                <Input value={url} isReadOnly />
                <InputRightElement>
                  <IconTextButton
                    aria-label={t('common.copy')}
                    icon={<CopyIcon size={20} />}
                    onClick={copyUrl}
                  />
                </InputRightElement>
              </InputGroup>
              <Button
                ml={1}
                leftIcon={<EditIcon size={20} />}
                onClick={slugModal.onOpen}
              >
                {t('common.edit')}
              </Button>
            </Flex>
          </FormControl>

          <FormControl>
            <FormLabel>{t('OrgEditModal.defaultGraphView')}</FormLabel>
            <GraphViewsSelect
              value={{
                view: watch('defaultGraphView'),
                folded: watch('defaultGraphFolded'),
                members: watch('defaultGraphMembers'),
              }}
              onChange={({ view, folded, members }) => {
                setValue('defaultGraphView', view)
                setValue('defaultGraphFolded', folded)
                setValue('defaultGraphMembers', members)
              }}
              variant="outline"
              // The whiteboard can be the default view where it is on (Yohan,
              // 02/11/2026, #210): the dashboard preview draws the circles
              // instead, and an org switched off falls back to the default.
              whiteboardOption
            />
          </FormControl>

          <FormControl>
            <FormLabel>{t('OrgEditModal.governance')}</FormLabel>
            <Controller
              name="governanceMode"
              control={control}
              render={({ field }) => (
                <GovernanceModeSelect
                  value={field.value}
                  onChange={field.onChange}
                  variant="outline"
                  isDisabled={!isOwner}
                />
              )}
            />
          </FormControl>

          <FormControl>
            <FormLabel>{t('OrgEditModal.asyncGovernanceHeading')}</FormLabel>
            <Controller
              name="asyncGovernanceEnabled"
              control={control}
              render={({ field }) => (
                <AsyncGovernanceSwitch
                  value={!!field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  isDisabled={!isAdmin}
                />
              )}
            />
            <FormHelperText>
              {t('OrgEditModal.asyncGovernanceHelp')}
            </FormHelperText>
          </FormControl>

          <Collapse in={asyncGovernanceEnabled} unmountOnExit>
            <VStack spacing={10} align="start">
              <FormControl isInvalid={!!errors.proposalDurationDays}>
                <FormLabel>{t('OrgEditModal.proposalDuration')}</FormLabel>
                <Controller
                  name="proposalDurationDays"
                  control={control}
                  render={({ field }) => (
                    <ProposalDurationInput
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      isDisabled={!isAdmin}
                    />
                  )}
                />
                <FormHelperText>
                  {t('OrgEditModal.proposalDurationHelp')}
                </FormHelperText>
                <FormErrorMessage>
                  {t('OrgEditModal.proposalDurationError', {
                    min: minProposalDurationDays,
                    max: maxProposalDurationDays,
                  })}
                </FormErrorMessage>
              </FormControl>

              <FormControl>
                <FormLabel>{t('OrgEditModal.proposalDecisionModes')}</FormLabel>
                <Controller
                  name="proposalDecisionModes"
                  control={control}
                  render={({ field }) => (
                    <ProposalDecisionModesCheckboxes
                      value={field.value ?? ['consent']}
                      onChange={field.onChange}
                      isDisabled={!isAdmin}
                    />
                  )}
                />
                <FormHelperText>
                  {t('OrgEditModal.proposalDecisionModesHelp')}
                </FormHelperText>
              </FormControl>
            </VStack>
          </Collapse>

          {/* The meeting step aids of #136: on by default */}
          <FormControl>
            <FormLabel>{t('OrgEditModal.meetingAidsHeading')}</FormLabel>
            <Controller
              name="meetingAidsEnabled"
              control={control}
              render={({ field }) => (
                <Switch
                  isChecked={!!field.value}
                  onChange={(event) => field.onChange(event.target.checked)}
                  onBlur={field.onBlur}
                  isDisabled={!isAdmin}
                >
                  {t('OrgEditModal.meetingAids')}
                </Switch>
              )}
            />
            <FormHelperText>{t('OrgEditModal.meetingAidsHelp')}</FormHelperText>
          </FormControl>

          <Flex w="100%" justify="space-between" pt={4}>
            <Button
              colorScheme="red"
              variant="ghost"
              onClick={deleteModal.onOpen}
            >
              {t('common.delete')}
            </Button>
            <Button colorScheme="blue" type="submit">
              {t('common.save')}
            </Button>
          </Flex>
        </VStack>
      </form>

      {deleteModal.isOpen && (
        <OrgDeleteModal id={orgId} isOpen onClose={deleteModal.onClose} />
      )}

      {slugModal.isOpen && (
        <OrgSlugModal id={orgId} isOpen onClose={slugModal.onClose} />
      )}

      {strictModal.isOpen && strictSwitchSummary && (
        <StrictGovernanceModal
          isOpen
          onClose={handleStrictCancel}
          summary={strictSwitchSummary}
          onConfirm={handleStrictConfirm}
        />
      )}
    </>
  )
}
